<script>
  import { onMount } from 'svelte';
  import { page } from '$stores/pageStore';
  import RegulatorySection from '$lib/RegulatorySection.svelte';
  
  const API_BASE_URL = '/api/proxy';
  
  let regulatoryData = {};
  let intendedUse = '';
  let isLoading = true;
  let error = null;
  
  // Get the app parameter from URL (e.g., ?app=app, ?app=ios, ?app=android)
  // Default to 'app' (portal) if no parameter is provided
  $: appParam = $page.url.searchParams.get('app') || 'app';
  $: selectedApp = appParam === 'app' ? 'portal' : appParam === 'ios' ? 'ios' : appParam === 'android' ? 'android' : 'portal';
  $: selectedData = regulatoryData[selectedApp];
  
  onMount(async () => {
    await fetchRegulatoryData();
  });
  
  async function fetchRegulatoryData() {
    try {
      isLoading = true;
      error = null;
      
      const response = await fetch(`${API_BASE_URL}/regulatory`);
      
      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response error:', response.status, errorText);
        throw new Error(`Failed to fetch regulatory data: ${response.status} ${response.statusText}`);
      }
      
      // Get response as text first to check if it's JSON
      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error('Server returned invalid JSON response');
      }
      
      if (result.success) {
        regulatoryData = result.data || {};
        intendedUse = result.intendedUse || '';
      } else {
        throw new Error(result.message || 'Failed to fetch regulatory data');
      }
    } catch (err) {
      console.error('Error fetching regulatory data:', err);
      error = err.message;
      // Fallback to empty data
      regulatoryData = {};
      intendedUse = '';
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="w-full min-h-screen bg-white">
{#if isLoading}
  <div class="flex justify-center items-center min-h-[400px]">
    <p class="text-gray-600">Loading regulatory information...</p>
  </div>
{:else if error}
  <div class="flex justify-center items-center min-h-[400px]">
    <p class="text-red-600">Error loading regulatory information: {error}</p>
  </div>
{:else}
  <!-- Regulatory Information Section -->
  <section class="py-0 w-full flex-shrink-0 px-4 sm:px-6 lg:px-8">
    <div class="max-w-6xl mx-auto flex flex-col items-center">
      <h2 class="text-xl font-bold text-slate-700 mb-6 pb-2 border-b-2 border-slate-300 text-center">Regulatory Information</h2>
      
      {#if selectedData}
        <div class="w-full flex justify-center">
          <RegulatorySection data={selectedData} />
        </div>
      {:else}
        <div class="rounded-lg p-8 text-center">
          <p class="text-gray-600">Invalid app parameter. Please use ?app=app, ?app=ios, or ?app=android</p>
        </div>
      {/if}
    </div>
  </section>

  <!-- Intended Use Section -->
  <section class="py-6 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-xl font-bold text-slate-700 mb-4 text-center">Intended Use</h2>
      <p class="text-sm text-gray-700 leading-relaxed">
        {intendedUse || 'Intended use information not available.'}
      </p>
    </div>
  </section>
{/if}
</div>

